import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';
import { MOTION_GRAPHICS_SCENE_DESIGN_ID } from 'js/src/agent/tools/motion-graphics/motionGraphicsSceneDesign';
import Flags from 'js/src/libs/flags';

/**
 * When a chart scene is the right design, offered by list_scene_designs ahead of the template's
 * designs. It sits where the agent picks a design so that it competes with the stat designs
 * directly: as a pointer in each recipe it reached only the recipes that carried it, and still lost
 * to their stock-design options.
 */
export const CHART_SCENE_DESIGN_ENTRY = `### Chart scene — scene_design_id "${MOTION_GRAPHICS_SCENE_DESIGN_ID}"
Numbers the viewer should compare or follow — a change over time, a ranking, parts of a whole, a
before and after, two figures side by side — and any chart the user asks for go in a chart scene,
which a chart subagent designs to match the video. Give each set of figures worth comparing its own
chart scene. Don't put them in a stat design or text, or in a template design that has a chart
already drawn in: no tool can change that chart's values. A single figure said in passing stays
text in a stock design. Load \`${SKILL_NAMES.chartScenes}\` before adding one.`;

/**
 * For where a document's own figures are placed as media. Without it, showing a deck's charts as
 * images satisfies that rule and no chart scene is made. Empty without the flag.
 */
export function documentChartsPointer(indent: string): string {
    return Flags.canUseCodeoSubagents() ? `\n${indent}${DOCUMENT_CHARTS_POINTER}` : '';
}

const DOCUMENT_CHARTS_POINTER = `**A chart in the document whose values you can read off it** — bars, a line, shares of a whole — **is rebuilt as a chart scene from those exact values, not shown as an image**: load \`${SKILL_NAMES.chartScenes}\`. Show the original only for a table, a diagram, a map, or a chart too dense to read exactly.`;

/**
 * A skill as read by an agent that isn't offered `chart-scenes` (a subagent, which can't start the
 * chart subagent): the pointer would make it require a chart scene it can't make.
 */
export function withoutDocumentChartsPointer<T extends { instructions: string }>(skill: T): T {
    return {
        ...skill,
        get instructions() {
            return skill.instructions.replace(
                new RegExp(`\\n[ \\t]*${escapeRegExp(DOCUMENT_CHARTS_POINTER)}`),
                '',
            );
        },
    };
}

function escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const chartScenesSkill: Skill = {
    name: SKILL_NAMES.chartScenes,
    description:
        'Animated chart scenes — bars, lines, comparisons, counters — designed by a chart subagent ' +
        'to match the video. Load when the content has numbers worth showing (figures, a table, ' +
        'a CSV, a stat-heavy document) or the user asks for a chart.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        // Only an agent that authors motion graphics itself can be building a video whose design
        // system a delegated chart would break away from.
        const ownMotionGraphicsVideo = Flags.canUseCodeo()
            ? `

**Except in a motion graphics video you are building yourself** — one that has a design system:
author its charts like every other graphic, so they stay part of its one continuous design. This
skill is for a chart in any other video.`
            : '';
        return `# Chart Scenes

A chart scene is an animated motion graphic built from the numbers in the user's content. You add
the scene empty; a chart subagent designs it whole — the chart and its title — to fit the rest of
the video. **You don't design it yourself.**${ownMotionGraphicsVideo}

## The build
1. **Add the scene.** \`${TOOL_NAMES.addScene}\` with \`scene_design_id\` \`"${MOTION_GRAPHICS_SCENE_DESIGN_ID}"\`
   for a scene of its own, or \`${TOOL_NAMES.addVoiceoverScene}\` with the same id over the words
   that state the numbers in a voiceover. It shows nothing until it is designed. A scene of its own
   then needs its length, which the chart is paced to: set it with \`${TOOL_NAMES.setSceneDuration}\`
   before handing it over. One over a voiceover lasts as long as its words.
2. **Hand it to the subagent.** \`${TOOL_NAMES.runSubagent}\` with \`type\` \`chart\` and
   \`scene_ids\` set to the new scene's scene_id. Several chart scenes can be designed in parallel,
   one call each in the same message, even over the same voiceover. It sees neither the video nor
   your documents, only its \`prompt\` — plus the video's palette and fonts, how the scenes either
   side look and the voiceover over its scene, which it is given itself. So the \`prompt\` says:
   - what the scene says, in a sentence: "revenue doubled in two years, led by EMEA";
   - the figures, exactly — values, labels, units, time periods, pasted in, so the chart is
     accurate;
   - anything the user asked for ("compare 2024 to 2025"), with any wording or figures they want
     on screen.
   It chooses the title, which figures to label and what else goes on screen, keeping it to what
   a viewer can read in passing, so don't write its copy or ask for every value to be shown.
3. **Check it.** Review the scene the way you review any other. To change it — the user asks for
   different colors, a figure was wrong — start a chart subagent on the same scene again, saying
   what to change. It revises the existing design; never edit a chart's HTML yourself.

## When the video's frame changes
A chart scene keeps its design through an aspect ratio switch, still laid out for the old frame.
Start a chart subagent on each, all in the same message, asking it to lay the chart out again for
the new frame — give both frame sizes — keeping its data, copy, colours, type and the order and
pacing of its reveal.`;
    },
};
